import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2, Eye, EyeOff, AlertCircle, Check, Shield } from 'lucide-react';
import { api, errorMessage } from '../lib/api';

const strength = (p) => {
  if (!p) return { label: '', pct: 0, color: 'bg-white/10' };
  let score = 0;
  if (p.length >= 8) score++;
  if (p.length >= 12) score++;
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) score++;
  if (/\d/.test(p)) score++;
  if (/[^A-Za-z0-9]/.test(p)) score++;
  const levels = [
    { label: 'Too short', pct: 15, color: 'bg-red-500' },
    { label: 'Weak',      pct: 30, color: 'bg-red-500' },
    { label: 'Fair',      pct: 55, color: 'bg-amber-500' },
    { label: 'Good',      pct: 75, color: 'bg-emerald-400' },
    { label: 'Strong',    pct: 100, color: 'bg-emerald-400' },
  ];
  return levels[Math.min(score, levels.length - 1)];
};

const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [showC, setShowC] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const s = strength(pwd);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!token) { setErr('Reset link is missing or malformed.'); return; }
    if (pwd.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    if (pwd !== confirm) { setErr('Passwords do not match.'); return; }
    setSaving(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate('/login'), 1500);
    } catch (ex) {
      setErr(errorMessage(ex, 'Could not reset password'));
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0d] text-white flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-8">
          <span
            className="text-[13px] tracking-[0.32em] font-semibold uppercase bg-clip-text text-transparent select-none"
            style={{ backgroundImage: 'linear-gradient(90deg, #22d3ee 0%, #a855f7 100%)' }}
          >
            Pricing Studio
          </span>
        </Link>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 mb-3">
            <Shield className="w-3 h-3 text-emerald-300" />
            <span className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/60">Secure reset</span>
          </div>
          <h1 className="text-2xl font-semibold text-white">Choose a new password</h1>
          <p className="mt-1 text-sm text-white/55">Pick something strong — at least 8 characters.</p>

          {done ? (
            <div className="mt-6 flex items-center gap-2 text-emerald-300 text-[14px]">
              <Check className="w-4 h-4" /> Password updated. Redirecting to sign in…
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-password-form">
              <div>
                <label className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">New password</label>
                <div className="mt-1.5 relative">
                  <input
                    type={show ? 'text' : 'password'}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    placeholder="At least 8 characters"
                    autoFocus
                    data-testid="rp-password"
                    className="w-full h-11 pl-3 pr-11 rounded-lg bg-white/[0.03] border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-400/50"
                  />
                  <button type="button" onClick={() => setShow((v) => !v)} tabIndex={-1} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white">
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {pwd && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1 rounded-full bg-white/5 overflow-hidden"><div className={`h-full ${s.color}`} style={{ width: `${s.pct}%` }} /></div>
                    <span className="text-[11px] text-white/50">{s.label}</span>
                  </div>
                )}
              </div>
              <div>
                <label className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">Confirm password</label>
                <div className="mt-1.5 relative">
                  <input
                    type={showC ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repeat new password"
                    data-testid="rp-confirm"
                    className="w-full h-11 pl-3 pr-11 rounded-lg bg-white/[0.03] border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-400/50"
                  />
                  <button type="button" onClick={() => setShowC((v) => !v)} tabIndex={-1} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white">
                    {showC ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {err && <div className="flex items-start gap-2 text-[13px] text-red-300"><AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{err}</span></div>}
              <button type="submit" disabled={saving} data-testid="rp-submit" className="w-full h-11 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset password'}
              </button>
              <div className="text-center">
                <Link to="/login" className="text-[12px] text-white/50 hover:text-white/80">Back to sign in</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
